import React from 'react';
import { X, Image as ImageIcon, BookOpen } from 'lucide-react';

interface EasiGuideModalProps {
  onClose: () => void;
}

export default function EasiGuideModal({ onClose }: EasiGuideModalProps) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/50 backdrop-blur-sm animate-fade-in">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-6xl max-h-[95vh] flex flex-col overflow-hidden animate-slide-up">
        {/* Header */}
        <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between sticky top-0 bg-white z-10">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-blue-50 flex items-center justify-center text-blue-600">
              <BookOpen className="w-5 h-5" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-slate-800">EASI User Guide & Atlas</h2>
              <p className="text-sm text-slate-500">Eczema Area and Severity Index</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-lg transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6 space-y-8">
          
          <section className="space-y-4">
            <h3 className="text-lg font-bold text-slate-800 flex items-center gap-2">
              <span className="flex items-center justify-center w-6 h-6 rounded-full bg-blue-100 text-blue-700 text-sm">1</span>
              Assess the severity of each of the four signs
            </h3>
            <p className="text-slate-600 text-sm leading-relaxed">
              Grade the severity of each sign (Erythema, Edema/Papulation, Excoriation, Lichenification) on a scale of 0 to 3:
            </p>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {[
                { grade: 0, label: 'None' },
                { grade: 1, label: 'Mild' },
                { grade: 2, label: 'Moderate' },
                { grade: 3, label: 'Severe' },
              ].map(s => (
                <div key={s.grade} className="bg-slate-50 border border-slate-200 rounded-lg p-3 text-center">
                  <div className="text-xl font-bold text-slate-800 mb-1">{s.grade}</div>
                  <div className="text-sm font-semibold text-slate-600">{s.label}</div>
                </div>
              ))}
            </div>
            <ul className="list-disc pl-5 text-sm text-slate-600 space-y-2 mt-2">
              <li>Take an average of the severity across the involved region.</li>
              <li>Half points (1.5 and 2.5) may be used. 0.5 is not permitted – if a sign is present it should be at least mild (1).</li>
              <li>Palpation may be useful in assessing edema/papulation as well as lichenification.</li>
            </ul>
          </section>

          <hr className="border-slate-100" />

          <section className="space-y-4">
            <h3 className="text-lg font-bold text-slate-800 flex items-center gap-2">
              <ImageIcon className="w-5 h-5 text-indigo-500" />
              Lesion Severity Photographic Atlas
            </h3>
            <p className="text-slate-600 text-sm">
              To aid your severity grading, refer to the photographic atlas below. 
            </p>
            
            <div className="bg-slate-50 rounded-xl border-2 border-dashed border-slate-300 p-4 flex items-center justify-center relative group">
              <img 
                src="/easi-severity-atlas.png?v=2" 
                alt="EASI Lesion Severity Atlas"
                className="w-full h-auto object-contain rounded-lg shadow-sm"
                onError={(e) => {
                  const target = e.target as HTMLImageElement;
                  target.style.display = 'none';
                  target.parentElement?.classList.add('flex-col', 'gap-3', 'min-h-[300px]');
                  target.nextElementSibling?.classList.remove('hidden');
                }}
              />
              <div className="hidden flex-col items-center text-center text-slate-400">
                <ImageIcon className="w-12 h-12 mb-2 text-slate-300" />
                <p className="font-medium text-slate-500">Image not found</p>
                <p className="text-xs">Save the atlas image from the PDF as <code className="bg-slate-200 px-1 py-0.5 rounded text-slate-700">public/easi-severity-atlas.png</code></p>
              </div>
            </div>
          </section>
          
          <hr className="border-slate-100" />

          <section className="space-y-4">
            <h3 className="text-lg font-bold text-slate-800 flex items-center gap-2">
              <ImageIcon className="w-5 h-5 text-indigo-500" />
              Extent of Eczema per Body Region
            </h3>
            <div className="bg-slate-50 rounded-xl border-2 border-dashed border-slate-300 p-4 flex items-center justify-center relative">
              <img 
                src="/easi-extent-atlas.png?v=2" 
                alt="EASI Body Region Extent"
                className="w-full h-auto object-contain mx-auto rounded-lg shadow-sm"
                onError={(e) => {
                  const target = e.target as HTMLImageElement;
                  target.style.display = 'none';
                  target.parentElement?.classList.add('flex-col', 'gap-3', 'min-h-[300px]');
                  target.nextElementSibling?.classList.remove('hidden');
                }}
              />
              <div className="hidden flex-col items-center text-center text-slate-400">
                <ImageIcon className="w-12 h-12 mb-2 text-slate-300" />
                <p className="font-medium text-slate-500">Image not found</p>
                <p className="text-xs">Save the extent diagram from the PDF as <code className="bg-slate-200 px-1 py-0.5 rounded text-slate-700">public/easi-extent-atlas.png</code></p>
              </div>
            </div>
          </section>

          <hr className="border-slate-100" />

          <section className="space-y-4">
            <h3 className="text-lg font-bold text-slate-800 flex items-center gap-2">
              <span className="flex items-center justify-center w-6 h-6 rounded-full bg-blue-100 text-blue-700 text-sm">2</span>
              Age-based Multipliers (Children vs Adults)
            </h3>
            <p className="text-slate-600 text-sm leading-relaxed">
              When calculating the final EASI score, the multipliers for the body regions differ depending on the patient's age to reflect the relative proportion of body surface area:
            </p>
            <div className="overflow-x-auto">
              <table className="w-full text-sm text-left border border-slate-200">
                <thead className="bg-slate-50 text-slate-700 border-b border-slate-200">
                  <tr>
                    <th className="px-4 py-2 font-bold">Body Region</th>
                    <th className="px-4 py-2 font-bold text-center">≥ 8 Years Old</th>
                    <th className="px-4 py-2 font-bold text-center">&lt; 8 Years Old</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-200 text-slate-600">
                  <tr>
                    <td className="px-4 py-2 font-medium">Head and Neck</td>
                    <td className="px-4 py-2 text-center">0.1</td>
                    <td className="px-4 py-2 text-center bg-blue-50 font-bold text-blue-700">0.2</td>
                  </tr>
                  <tr>
                    <td className="px-4 py-2 font-medium">Upper Limbs</td>
                    <td className="px-4 py-2 text-center">0.2</td>
                    <td className="px-4 py-2 text-center">0.2</td>
                  </tr>
                  <tr>
                    <td className="px-4 py-2 font-medium">Trunk</td>
                    <td className="px-4 py-2 text-center">0.3</td>
                    <td className="px-4 py-2 text-center">0.3</td>
                  </tr>
                  <tr>
                    <td className="px-4 py-2 font-medium">Lower Limbs</td>
                    <td className="px-4 py-2 text-center">0.4</td>
                    <td className="px-4 py-2 text-center bg-orange-50 font-bold text-orange-700">0.3</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <p className="text-xs text-slate-500 italic mt-2">
              * The calculator in this app will automatically adjust these multipliers when you select the patient's age category.
            </p>
          </section>

          <div className="bg-blue-50 border border-blue-100 rounded-xl p-4 flex gap-3">
            <div className="text-blue-600 mt-0.5">
              <BookOpen className="w-5 h-5" />
            </div>
            <div className="text-sm text-blue-900 leading-relaxed">
              <strong>Remember:</strong> Include only inflamed areas in your assessment; do not include xerosis (dryness), ichthyosis, keratosis pilaris, urticaria, infection (unless there is underlying eczema), or post inflammatory pigmentation changes.
            </div>
          </div>

        </div>
      </div>
    </div>
  );
}
